import { User, Bot, Check, AlertCircle, Loader2, ExternalLink, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import type { Message } from '../types/agent';

// Product card interface for PRODUCT_SEARCH results
interface ProductCard {
  name: string;
  slug: string | null;
  image_url: string | null;
  price: number | null;
  currency: string;
}

interface ChatMessageProps {
  message: Message;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser
            ? 'bg-gradient-primary'
            : 'bg-gradient-to-br from-teal-400 to-cyan-500'
        }`}
      >
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 min-w-0 ${isUser ? 'text-right' : ''}`}>
        <div
          className={`inline-block max-w-full rounded-2xl px-4 py-2.5 ${
            isUser
              ? 'bg-gradient-primary text-white rounded-tr-md'
              : message.isError
              ? 'bg-red-50 text-red-700 border border-red-200 rounded-tl-md'
              : 'bg-gray-100 text-gray-900 rounded-tl-md'
          }`}
        >
          {/* Message content */}
          {isUser ? (
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
              {message.content}
            </p>
          ) : message.isThinking ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="animate-pulse">Nachdenken...</span>
            </div>
          ) : (
            <div className="prose prose-sm prose-gray max-w-none break-words">
              <ReactMarkdown
                components={{
                  // Paragraphs
                  p: ({ children }) => (
                    <p className="text-sm leading-relaxed mb-2 last:mb-0">{children}</p>
                  ),
                  // Bold
                  strong: ({ children }) => (
                    <strong className="font-semibold">{children}</strong>
                  ),
                  // Italic
                  em: ({ children }) => (
                    <em className="italic">{children}</em>
                  ),
                  // Lists
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside mb-2 space-y-1 text-sm">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside mb-2 space-y-1 text-sm">{children}</ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-sm">{children}</li>
                  ),
                  // Code
                  code: ({ children, className }) => {
                    const isInline = !className;
                    return isInline ? (
                      <code className="bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">
                        {children}
                      </code>
                    ) : (
                      <code className="block bg-gray-800 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-2">
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-2">
                      {children}
                    </pre>
                  ),
                  // Headings
                  h1: ({ children }) => (
                    <h1 className="text-base font-bold mb-2 mt-3 first:mt-0">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-sm font-bold mb-2 mt-3 first:mt-0">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-sm font-semibold mb-1 mt-2 first:mt-0">{children}</h3>
                  ),
                  // Links
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:text-teal-700 underline"
                    >
                      {children}
                    </a>
                  ),
                  // Blockquote
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-gray-300 pl-3 my-2 text-gray-600 italic">
                      {children}
                    </blockquote>
                  ),
                  // Horizontal rule
                  hr: () => <hr className="my-3 border-gray-300" />,
                }}
              >
                {message.content}
              </ReactMarkdown>
              {message.isStreaming && (
                <span className="inline-block w-1.5 h-4 ml-0.5 bg-gray-500 animate-pulse rounded-full" />
              )}
            </div>
          )}
        </div>

        {/* Tool calls */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-2 space-y-2">
            {message.toolCalls.map((tc) => (
              <div key={tc.id}>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {tc.status === 'pending' ? (
                    <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                  ) : tc.status === 'success' ? (
                    <Check className="w-3 h-3 text-emerald-500" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-red-500" />
                  )}
                  <span className="font-medium">
                    {getToolLabel(tc.name)}
                  </span>
                  {tc.status === 'success' && tc.result?.data && (
                    <span className="text-gray-400">
                      {getToolResultSummary(tc.name, tc.result.data)}
                    </span>
                  )}
                  {tc.status === 'failed' && tc.result?.error && (
                    <span className="text-red-400">{tc.result.error}</span>
                  )}
                </div>
                {/* Product Cards for PRODUCT_SEARCH */}
                {tc.name === 'PRODUCT_SEARCH' && tc.status === 'success' && tc.result?.data?.products && (
                  <ProductCards products={tc.result.data.products as unknown as ProductCard[]} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <div
          className={`mt-1 text-xs text-gray-400 ${isUser ? 'text-right' : ''}`}
        >
          {formatTime(message.createdAt)}
        </div>
      </div>
    </div>
  );
}

// Product Cards Component
function ProductCards({ products }: { products: ProductCard[] }) {
  if (!products || products.length === 0) return null;

  const formatPrice = (price: number | null, currency: string) => {
    if (price === null) return null;
    // Handle invalid currency codes (symbols instead of ISO codes)
    let currencyCode = currency || 'EUR';
    if (currencyCode === '€') currencyCode = 'EUR';
    if (currencyCode === '$') currencyCode = 'USD';
    if (currencyCode === '£') currencyCode = 'GBP';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currencyCode,
    }).format(price);
  };

  return (
    <div className="mt-2 space-y-2">
      {products.map((product, index) => (
        <div
          key={product.slug || index}
          className="flex items-center gap-3 p-2 bg-white border border-gray-200 rounded-lg shadow-sm"
        >
          {/* Product Image */}
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Package className="w-6 h-6 text-gray-400" />
            </div>
          )}

          {/* Product Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">
              {product.name}
            </p>
            {product.price && (
              <p className="text-xs text-gray-500">
                {formatPrice(product.price, product.currency)}
              </p>
            )}
          </div>

          {/* Link to Product */}
          {product.slug && (
            <Link
              to={`/products/${product.slug}`}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded transition-colors"
            >
              <span>Zum Produkt</span>
              <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}

function getToolLabel(name: string): string {
  switch (name) {
    // Product search
    case 'PRODUCT_SEARCH':
      return 'Produktsuche';
    // Lead actions
    case 'LEAD_CREATE':
      return 'Lead erstellen';
    case 'LEAD_UPDATE':
      return 'Lead aktualisieren';
    case 'LEAD_ADD_NOTE':
      return 'Notiz hinzufügen';
    case 'LEAD_GET':
      return 'Lead abrufen';
    // Todo actions
    case 'TODO_CREATE':
      return 'Aufgabe erstellen';
    case 'TODO_COMPLETE':
      return 'Aufgabe erledigen';
    case 'TODO_RESCHEDULE':
      return 'Aufgabe verschieben';
    case 'TODO_LIST':
      return 'Aufgaben auflisten';
    // Calendar actions
    case 'CAL_EVENT_CREATE':
      return 'Termin erstellen';
    case 'CAL_EVENT_UPDATE':
      return 'Termin aktualisieren';
    case 'CAL_EVENT_DELETE':
      return 'Termin löschen';
    case 'CAL_EVENT_LIST':
      return 'Termine abrufen';
    // Form actions
    case 'FORM_LIST':
      return 'Formulare auflisten';
    case 'FORM_GET':
      return 'Formular abrufen';
    case 'FORM_CREATE':
      return 'Formular erstellen';
    case 'FORM_UPDATE':
      return 'Formular aktualisieren';
    case 'FORM_FIELD_ADD':
      return 'Feld hinzufügen';
    case 'FORM_FIELD_UPDATE':
      return 'Feld aktualisieren';
    case 'FORM_FIELD_REMOVE':
      return 'Feld entfernen';
    case 'FORM_PUBLISH':
      return 'Formular veröffentlichen';
    // Site/Page actions
    case 'SITE_PAGES_LIST':
      return 'Seiten auflisten';
    case 'SITE_PAGE_GET':
      return 'Seite abrufen';
    case 'SITE_PAGE_UPDATE_SETTINGS':
      return 'Seite aktualisieren';
    case 'SITE_BLOCK_UPDATE':
      return 'Block aktualisieren';
    case 'SITE_BLOCK_REORDER':
      return 'Blöcke sortieren';
    case 'SITE_PAGE_PUBLISH':
      return 'Seite veröffentlichen';
    // Analytics actions (read-only)
    case 'ANALYTICS_OVERVIEW':
      return 'Analytics Übersicht';
    case 'ANALYTICS_PAGE_PERFORMANCE':
      return 'Seiten-Performance';
    case 'ANALYTICS_FORM_PERFORMANCE':
      return 'Formular-Performance';
    case 'ANALYTICS_TRAFFIC_SOURCES':
      return 'Traffic-Quellen';
    default:
      return name;
  }
}

function getToolResultSummary(name: string, data: Record<string, unknown>): string {
  // Product search results
  if (name === 'PRODUCT_SEARCH') {
    const products = data.products as Array<unknown> | undefined;
    if (products && products.length > 0) {
      return `→ ${products.length} Produkt(e) gefunden`;
    }
    return '→ Keine Produkte gefunden';
  }

  // Lead results
  if (data.leadName) {
    if (name === 'LEAD_UPDATE' && data.updatedFields) {
      const fields = data.updatedFields as string[];
      return `→ ${data.leadName} (${fields.join(', ')})`;
    }
    if (name === 'LEAD_ADD_NOTE') {
      return `→ Notiz bei ${data.leadName}`;
    }
    return `→ ${data.leadName}`;
  }
  if (data.lead) {
    const lead = data.lead as { full_name: string };
    return `→ ${lead.full_name}`;
  }
  if (data.leads) {
    const leads = data.leads as Array<{ full_name: string }>;
    return `→ ${leads.length} Lead(s) gefunden`;
  }

  // Todo results
  if (data.todoTitle) {
    if (name === 'TODO_RESCHEDULE' && data.newDueDate) {
      return `→ ${data.todoTitle} (${data.newDueDate})`;
    }
    return `→ ${data.todoTitle}`;
  }
  if (data.todos) {
    const todos = data.todos as Array<unknown>;
    return `→ ${todos.length} Aufgabe(n)`;
  }

  // Calendar results
  if (data.eventTitle) {
    if (name === 'CAL_EVENT_UPDATE') {
      return `→ ${data.eventTitle} aktualisiert`;
    }
    if (name === 'CAL_EVENT_DELETE') {
      return `→ ${data.eventTitle} gelöscht`;
    }
    return `→ ${data.eventTitle}`;
  }
  if (data.events) {
    const events = data.events as Array<unknown>;
    return `→ ${events.length} Termin(e)`;
  }

  // Form results
  if (data.formName) {
    if (name === 'FORM_PUBLISH') {
      return data.isPublished ? `→ ${data.formName} veröffentlicht` : `→ ${data.formName} deaktiviert`;
    }
    return `→ ${data.formName}`;
  }
  if (data.forms) {
    const forms = data.forms as Array<unknown>;
    return `→ ${forms.length} Formular(e)`;
  }
  if (data.fieldLabel) {
    return `→ ${data.fieldLabel}`;
  }

  // Site/Page results
  if (data.pageTitle) {
    if (name === 'SITE_PAGE_PUBLISH') {
      return data.isPublished ? `→ ${data.pageTitle} veröffentlicht` : `→ ${data.pageTitle} deaktiviert`;
    }
    return `→ ${data.pageTitle}`;
  }
  if (data.pages) {
    const pages = data.pages as Array<unknown>;
    return `→ ${pages.length} Seite(n)`;
  }
  if (data.blockType) {
    return `→ ${data.blockType} Block`;
  }

  // Analytics results
  if (name === 'ANALYTICS_OVERVIEW' && data.rangeLabel) {
    return `→ ${data.rangeLabel}`;
  }
  if (name === 'ANALYTICS_PAGE_PERFORMANCE' && data.pages) {
    const pages = data.pages as Array<unknown>;
    return `→ ${pages.length} Seite(n), ${data.rangeLabel || ''}`;
  }
  if (name === 'ANALYTICS_FORM_PERFORMANCE' && data.forms) {
    const forms = data.forms as Array<unknown>;
    return `→ ${forms.length} Formular(e), ${data.rangeLabel || ''}`;
  }
  if (name === 'ANALYTICS_TRAFFIC_SOURCES' && data.sources) {
    const sources = data.sources as Array<unknown>;
    return `→ ${sources.length} Quelle(n), ${data.rangeLabel || ''}`;
  }

  return '';
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
